import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useUserContext } from "../../../context/UserProvider";
import { db, storage } from "../../../firebase";
import ProfilePicture from "../../util/ProfilePicture";
import { CloseIcon, HashtagIcon, PaperPlaneIcon, CameraIcon, GraphIcon } from "../../util/Icons";
import DropDownMenu from "../../util/DropDownMenu";
import { ProgressComponent } from "./SocialFeed";
import { getPostFeedFilterKeys } from "./feedFilterUtils";
import { buildSocialProfileSearchFields } from "./socialProfileSearchUtils";
import { doc, setDoc, getDoc, collection, orderBy, query, where, getAggregateFromServer, sum, serverTimestamp } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { Spinner } from "react-bootstrap";

export default function PostInput({ addPost, categories }) {
    const { user, userDetails, activeRaces, userRaces } = useUserContext();
    const messageRef = useRef(null);
    const [buttonEnabled, setButtonEnabled] = useState(true);
    const [photos, setPhotos] = useState([]);
    const [hashtags, setHashTags] = useState([]);
    const [raceToShare, setRaceToShare] = useState(null);
    const [errorMsg, setErrorMsg] = useState("");
    const [showCategoryNudge, setShowCategoryNudge] = useState(false);
    const categoryDropdownRef = useRef(null);
    const maxPhotos = 6;
    const location = useLocation();

    useEffect(() => {
        const createdImage = location?.state?.createdImage;
        const createdImageUrl = location?.state?.createdImageUrl;
        const prefillPostMessage = location?.state?.prefillPostMessage;
        if (createdImage) {
            window.scrollTo(0, 0);
            setPhotos([createdImage]);
        } else if (createdImageUrl) {
            window.scrollTo(0, 0);
            setPhotos([createdImageUrl]);
        }
        if (prefillPostMessage && messageRef.current) {
            window.scrollTo(0, 0);
            messageRef.current.value = prefillPostMessage;
            messageRef.current.focus();
        }
    }, [location]);

    function getPhotoKey(photo) {
        if (typeof photo === "string") return photo;
        return `${photo.name}-${photo.size}-${photo.lastModified}`;
    }

    function getPhotoPreviewSrc(photo) {
        if (typeof photo === "string") return photo;
        return URL.createObjectURL(photo);
    }

    function getPhotoLabel(photo) {
        if (typeof photo === "string") return "Shared badge";
        return photo.name;
    }

    useEffect(() => {
        async function getTeamProgress() {
            if (!raceToShare) return;
            if (raceToShare?.teamProgress) return;
            const teamName = raceToShare?.teamName;
            if (typeof teamName === typeof "") {
                if (teamName !== "noTeam") {
                    // is in a team, get the team's progress
                    const q = query(
                        collection(db, "races", raceToShare.legId, "subscribedUsers"),
                        where("teamName", "==", teamName),
                        orderBy("totalDistance", "desc")
                    );
                    const snap = await getAggregateFromServer(q, { teamProgress: sum("totalDistance") });
                    const updatedRaceShare = { ...raceToShare };
                    updatedRaceShare.teamProgress = snap.data().teamProgress;
                    setRaceToShare(updatedRaceShare);
                }
            }
        }
        getTeamProgress();
    }, [raceToShare]);

    async function UploadPost() {
        // Validity checks
        if (!buttonEnabled) return;
        if (!messageRef.current) return;
        const message = messageRef?.current?.value;
        if (message.length < 1) {
            setErrorMsg("Message must be at least 1 character");
            return;
        }
        // Check if category is selected
        if (hashtags.length === 0) {
            setShowCategoryNudge(true);
            return;
        }
        setButtonEnabled(false);
        // Format the data
        const postRef = doc(collection(db, "social_feed"));
        const epochTime = Date.now();
        const postData = {
            authorID: user.uid,
            authorName: userDetails?.firstName + " " + userDetails?.lastName,
            message: message,
            createdAt: new Date(epochTime).toISOString(),
            epochTime,
            hashtags: [...hashtags],
            feedFilterKeys: getPostFeedFilterKeys(hashtags, userRaces, epochTime),
            count_likes: 0,
            count_comments: 0,
        };
        // Upload photos here
        if (photos.length > 0) {
            console.log(
                "Uploading photos",
                calcFileSize(
                    photos.reduce((prev, p) => {
                        if (typeof p === "string") return prev;
                        return prev + p.size;
                    }, 0)
                )
            );
            let newPicturesURLs = [];
            let newPicturesPaths = [];
            for (const p of photos) {
                if (typeof p === "string") {
                    newPicturesURLs.push(p);
                    continue;
                }
                let picPath = `social_feed/${user.uid}/${postRef.id}/${Date.now() + p.name}`;
                let picRef = ref(storage, picPath);
                await uploadBytes(picRef, p);
                const downLoadURL = await getDownloadURL(picRef);
                newPicturesURLs.push(downLoadURL);
                newPicturesPaths.push(picPath);
            }
            postData.pictures = newPicturesURLs;
            if (newPicturesPaths.length > 0) {
                postData.picturesRefs = newPicturesPaths;
            }
        }

        // Share race progress
        if (raceToShare !== null) {
            postData.shareRace = raceToShare;
        }
        // Write data to database
        // const postRef = await addDoc(collection(db, "social_feed"), postData);
        await setDoc(postRef, postData);
        await setDoc(
            doc(db, "social_profile", user.uid),
            {
                ...buildSocialProfileSearchFields(user.uid, postData.authorName, epochTime),
                updatedAt: serverTimestamp(),
            },
            { merge: true }
        );
        console.log("Posted!", postRef.id);
        console.log("Message length: ", message.length);
        console.log("Post data: ", postData);
        // Clear text box
        messageRef.current.value = "";
        if (location?.state?.createdImage) location.state.createdImage = null;
        if (location?.state?.createdImageUrl) location.state.createdImageUrl = null;
        if (location?.state?.prefillPostMessage) location.state.prefillPostMessage = null;
        setPhotos([]);
        setRaceToShare(null);
        setHashTags([]);
        // Add post to feed without refreshing all
        addPost((prev) => [{ id: postRef.id, ...postData }, ...prev]);
        // Add a little wait at the end
        await new Promise((resolve) => setTimeout(resolve, 2000));
        setButtonEnabled(true);
    }
    function ImageInput(e) {
        const files = e?.target?.files;
        if (!files) return;
        if (files?.length === 0) return;
        const newPhotos = [];
        for (const f of files) {
            if (newPhotos.length < maxPhotos) newPhotos.push(f);
        }
        setPhotos(newPhotos);
    }
    return (
        <div className="relative w-auto border border-slate-200 rounded-xl p-2 my-2 md:mx-2 box-border bg-white shadow-md grid grid-cols-[36px,1fr] gap-3">
            <ProfilePicture></ProfilePicture>
            <div className="w-auto min-w-0 flex flex-col">
                <textarea
                    ref={messageRef}
                    name=""
                    id=""
                    className="border h-20 rounded-lg px-2 py-2 resize-none"
                    placeholder="What's on your mind? Share your progress..."
                    onChange={() => setErrorMsg("")}
                ></textarea>
                {errorMsg && <p className="text-red-500 text-sm px-2 mt-1 mb-0 font-medium">{errorMsg}</p>}
                {typeof raceToShare === typeof {} && raceToShare !== null && (
                    <ProgressComponent
                        imgUrl={raceToShare?.raceInfo.imageUrl}
                        raceName={raceToShare?.raceInfo.name}
                        legName={raceToShare?.raceInfo.legName}
                        progress={raceToShare?.progress}
                        teamProgress={raceToShare?.teamProgress || null}
                        distance={raceToShare?.raceInfo.distance}
                    >
                        <span
                            className="absolute block right-2 p-1 text-red-600 cursor-pointer select-none"
                            onClick={() => {
                                setRaceToShare(null);
                            }}
                        >
                            <CloseIcon size={22} />
                        </span>
                    </ProgressComponent>
                )}
                {photos.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                        {photos.map((photo) => {
                            return (
                                <div className="relative" key={getPhotoKey(photo)}>
                                    <span
                                        className="absolute top-1 right-1 text-red-500 font-bold cursor-pointer w-6 h-6 flex items-center justify-center bg-red-100/90 hover:bg-red-200 rounded-full shadow-sm backdrop-blur-sm transition-colors"
                                        onClick={() => {
                                            const index = photos.findIndex((p) => getPhotoKey(p) === getPhotoKey(photo));
                                            if (index >= 0) {
                                                photos.splice(index, 1);
                                                setPhotos([...photos]);
                                            }
                                        }}
                                    >
                                        <span className="text-xs leading-none">✕</span>
                                    </span>
                                    <img
                                        className="w-auto h-20 inline-block rounded-xl object-cover border border-slate-200 shadow-sm"
                                        src={getPhotoPreviewSrc(photo)}
                                        alt={getPhotoLabel(photo)}
                                        title={getPhotoLabel(photo)}
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}
                {hashtags.length > 0 && (
                    <div className="mt-2 flex gap-2 overflow-x-auto flex-wrap">
                        {hashtags.map((hash) => {
                            return (
                                <span key={hash} className="text-blue-600 bg-blue-100 rounded-lg px-2 py-1 cursor-pointer">
                                    #{hash}
                                </span>
                            );
                        })}
                    </div>
                )}
                {/* Add to your post Bar */}
                <div className="border border-slate-200 rounded-lg flex justify-center items-center px-4 py-2 mt-2 shadow-sm relative">
                    <div className="flex gap-6 md:gap-8 items-center justify-center">
                        {/* Add Photos */}
                        <label
                            className="flex flex-col md:flex-row items-center gap-1 md:gap-2 text-green-500 hover:bg-slate-100 rounded-lg p-2 transition-colors cursor-pointer"
                            title="Add Photos"
                            htmlFor="image_upload_input"
                        >
                            <CameraIcon size={24} />
                            <span className="hidden md:block text-xs font-medium text-slate-600">Photos</span>
                            <span className="block md:hidden text-[10px] font-medium text-slate-600">Photos</span>
                            {/* Hidden File Input */}
                            <input
                                type="file"
                                id="image_upload_input"
                                name="image_upload_name"
                                accept="image/*"
                                max={maxPhotos}
                                maxLength={maxPhotos}
                                multiple
                                style={{ display: "none" }}
                                onInput={ImageInput}
                            />
                        </label>

                        {/* Share Progress */}
                        {activeRaces.length > 0 && (
                            <DropDownMenu
                                closeOnClick={true}
                                trigger={({ ref, onClick, open }) => {
                                    return (
                                        <button
                                            ref={ref}
                                            className="flex flex-col md:flex-row items-center gap-1 md:gap-2 text-blue-500 hover:bg-slate-100 rounded-lg p-2 transition-colors aria-pressed:bg-slate-200"
                                            onPointerDown={() => onClick()}
                                            title="Share Progress"
                                            aria-pressed={open}
                                        >
                                            <GraphIcon size={24} style="fill" />
                                            <span className="hidden md:block text-xs font-medium text-slate-600">Progress</span>
                                            <span className="block md:hidden text-[10px] font-medium text-slate-600">Progress</span>
                                        </button>
                                    );
                                }}
                            >
                                <div className="absolute top-0 right-0 z-20 bg-white border border-slate-200 shadow-xl rounded-xl p-3 w-64 anim-card origin-top">
                                    <p className="mb-1 select-none font-medium border-b-slate-500 border-b">Share Progress</p>
                                    {/* <hr className="m-0" /> */}
                                    <div className="flex w-full gap-3 h-full overflow-x-auto justify-center items-center py-1 ">
                                        <div className="flex flex-col w-max max-w-full h-full gap-2">
                                            {activeRaces.length <= 0 && <span>Register to a race and share it when its in progress</span>}
                                            {activeRaces.map((r) => {
                                                return (
                                                    <button
                                                        key={r?.legId}
                                                        className="cursor-pointer border hover:bg-slate-200 shadow-sm select-none rounded-lg grid grid-cols-[36px,minmax(0,1fr)] px-2 h-full justify-start gap-2 items-center py-2"
                                                        onClick={async (e) => {
                                                            e.target.disabled = true;
                                                            // console.log(r);
                                                            const progressSnap = await getDoc(doc(db, "races", r.legId, "subscribedUsers", user.uid)).catch((err) => {
                                                                console.log(err.message);
                                                            });
                                                            r.progress = progressSnap.data().totalDistance || 0;

                                                            setRaceToShare(r);
                                                            e.target.disabled = false;
                                                        }}
                                                    >
                                                        <img src={r.raceInfo.imageUrl} alt="logo" className="h-9 w-9 pointer-events-none " />
                                                        <div className="flex flex-col">
                                                            <p className="m-0 text-sm font-semibold pointer-events-none block whitespace-nowrap w-full text-clip overflow-clip">
                                                                {r.raceInfo.name}
                                                            </p>
                                                            <p className="m-0 text-sm pointer-events-none w-full">{r.raceInfo.legName}</p>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </DropDownMenu>
                        )}

                        {/* Add Categories */}
                        <div className="" ref={categoryDropdownRef}>
                            {/* Category Nudge Bubble */}
                            {showCategoryNudge && (
                                <div
                                    className="absolute -top-12 left-1/2 -translate-x-1/2 z-30 whitespace-nowrap bg-gradient-to-r from-purple-500 to-blue-500 text-white text-xs font-medium px-3 py-2 rounded-lg shadow-lg cursor-pointer hover:from-purple-600 hover:to-blue-600 transition-all"
                                    style={{ animation: 'bubbleIn 0.3s ease-out' }}
                                    onPointerDown={() => {
                                        setShowCategoryNudge(false);
                                        // Use setTimeout to ensure state update happens first
                                        setTimeout(() => {
                                            const button = categoryDropdownRef.current?.querySelector('button');
                                            if (button) {
                                                const event = new PointerEvent('pointerdown', { bubbles: true });
                                                button.dispatchEvent(event);
                                            }
                                        }, 10);
                                    }}
                                >
                                    <style>{`
                    @keyframes bubbleIn {
                      from { opacity: 0; transform: translateX(-50%) translateY(8px) scale(0.9); }
                      to { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
                    }
                  `}</style>
                                    Please add a category! ✨
                                    {/* Arrow pointing down */}
                                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-purple-500"></div>
                                </div>
                            )}
                            <DropDownMenu
                                closeOnClick={false}
                                trigger={({ ref, onClick, open }) => {
                                    return (
                                        <button
                                            ref={ref}
                                            className={`flex flex-col md:flex-row items-center gap-1 md:gap-2 text-yellow-500 hover:bg-slate-100 rounded-lg p-2 transition-colors aria-pressed:bg-slate-200 ${showCategoryNudge ? 'animate-pulse ring-2 ring-amber-400 ring-offset-2' : ''}`}
                                            onPointerDown={(e) => {
                                                // e.stopPropagation();
                                                setShowCategoryNudge(false);
                                                onClick();
                                            }}
                                            title="Add Categories"
                                            aria-pressed={open}
                                        >
                                            <HashtagIcon size={24} />
                                            <span className="hidden md:block text-xs font-medium text-slate-600">Categories</span>
                                            <span className="block md:hidden text-[10px] font-medium text-slate-600">Categories</span>
                                        </button>
                                    );
                                }}
                            >
                                <div className="absolute top-0 right-0 z-20 bg-white border border-slate-200 shadow-xl rounded-xl p-3 w-64 anim-card origin-top">
                                    <HashtagSelector
                                        options={categories}
                                        selectedOptions={hashtags}
                                        onSelect={(value) => {
                                            if (hashtags.includes(value)) {
                                                setHashTags((prev) => {
                                                    const updated = [...prev];
                                                    updated.splice(updated.indexOf(value), 1);
                                                    return updated;
                                                });
                                            } else {
                                                setHashTags((prev) => {
                                                    const updated = [...prev];
                                                    updated.push(value);
                                                    return updated;
                                                });
                                            }
                                        }}
                                    />
                                </div>
                            </DropDownMenu>
                        </div>
                    </div>
                </div>

                <button
                    className={`mt-2 font-medium py-1.5 px-4 rounded-lg w-full md:w-fit self-end flex gap-2 justify-center items-center transition-all duration-200 enabled:active:scale-95 shadow-sm text-sm ${buttonEnabled ? "bg-blue-500 hover:bg-blue-600 text-white" : "bg-slate-400 text-slate-200 cursor-not-allowed"
                        }`}
                    onClick={UploadPost}
                    disabled={!buttonEnabled}
                >
                    <>
                        <span>Post</span>
                        {buttonEnabled && <PaperPlaneIcon color={"white"} size={14} style={"stroke"} />}
                        {!buttonEnabled && <Spinner size="sm" />}
                    </>
                </button>
            </div>
        </div>
    );
}

function HashtagSelector({ options = [], selectedOptions = [], onSelect }) {
    return (
        <div>
            <p className="mb-1 select-none font-medium border-b-slate-500 border-b">Add Categories:</p>
            <div className="flex flex-wrap gap-1">
                {options.map((o) => {
                    return (
                        <button
                            key={o}
                            className={`border rounded-md py-1 px-3 text-sm font-medium transition-colors duration-200 cursor-pointer ${selectedOptions.includes(o)
                                ? "bg-blue-100 text-blue-600 border-blue-200"
                                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                                }`}
                            onClick={() => {
                                onSelect(o);
                            }}
                        >
                            <span>#{o[0].toUpperCase() + o.slice(1)}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/**
 *
 * @param {number} number
 * @returns
 */
function calcFileSize(number) {
    if (number < 1e3) {
        return `${number} bytes`;
    } else if (number >= 1e3 && number < 1e6) {
        return `${(number / 1e3).toFixed(1)} KB`;
    }
    return `${(number / 1e6).toFixed(1)} MB`;
}
